// packages/console/src/components/LiveTranscoder.tsx
//
// The production LIVE-transcoder console, ported from
// docs/design/ui_kits/aether-live/Transcoder.jsx and wired to REAL data off one
// workspace-scoped SSE stream (api/live streamLiveSession, via useLiveStream).
// Every simulated value from the prototype is gone (R10): the 500ms tick, the
// jitter walk, the fixed link/dest/log pools, the derived-GPU formula, and the
// hardcoded timecode are all replaced by live SSE data, and where the live
// backend is not reachable (it does not exist yet) the UI shows honest
// stopped / No-program / em-dash states rather than fabricated numbers.
//
// Layout, spacing, and the display conventions (mono tabular numerals, em dash
// for absent values, one signal blue, no shadows) are preserved from the
// prototype and the shared Parts primitives.

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SessionState } from "../hooks/useSession";
import { useLiveStream, sessionState } from "../hooks/useLiveStream";
import { useHardwareStream } from "../hooks/useTelemetry";
import { TopBar } from "./TopBar";
import { ReadinessTag } from "./Parts";
import { Icon } from "./Icons";
import type { ConsoleMode } from "./ModeToggle";
import { LiveIngestPanel } from "./panels/LiveIngestPanel";
import { LiveHardwarePanel } from "./panels/LiveHardwarePanel";
import { LiveProgramMonitor } from "./panels/LiveProgramMonitor";
import { LiveAggregatePanel } from "./panels/LiveAggregatePanel";
import { LiveEncoderPanel } from "./panels/LiveEncoderPanel";
import { LiveProbeGraph } from "./panels/LiveProbeGraph";
import { LiveDestinationsPanel } from "./panels/LiveDestinationsPanel";
import { LiveRenditionPanel } from "./panels/LiveRenditionPanel";
import { LivePresetPicker } from "./panels/LivePresetPicker";
import { LivePipelineLog } from "./panels/LivePipelineLog";
import { useLivePresets } from "../hooks/useLivePresets";
import { useLiveControl } from "../hooks/useLiveControl";
import type { LiveRung } from "../api/live";
import type { Tone } from "./Parts";
import { EM_DASH, fmtDur, mbps } from "../lib/format";

const HIST_LEN = 60; // egress sparkline ring buffer length

export function LiveTranscoder({
  session,
  mode,
  onMode,
}: {
  session: SessionState;
  mode: ConsoleMode;
  onMode: (m: ConsoleMode) => void;
}) {
  const live = useLiveStream();
  // The general GPU/hardware telemetry drives the readiness tag, so a healthy box
  // reads Ready even with no live stream (not Degraded).
  const hardware = useHardwareStream();
  // The authoritative session state is the latest "stream" sample, falling back
  // to the sticky "status" event (replayed on reconnect) before the first
  // sample lands, so the console reads on-air immediately after a reconnect.
  const onAir = sessionState(live) === "on_air";

  // ---- top-bar readiness tag (from the live hardware sample + transport) ----
  // Mirrors the file console's readiness logic against the live stream: Ready
  // when the stream is open and the GPU reports a utilisation sample, Degraded
  // while connecting, Unavailable when the transport errored.
  const readiness = useMemo<{ label: string; tone: "ok" | "warn" | "err" }>(() => {
    // Readiness is the BOX's GPU health (the same general hardware stream the file
    // console reads), not whether a live stream is running: a healthy encoder host
    // with no active stream is Ready to ingest, not Degraded. A live session's own
    // GPU sample refines "on air" elsewhere; it is not required to read Ready.
    const gpu = hardware.status?.gpu;
    if (gpu === "unavailable" || gpu === "error") return { label: "Unavailable", tone: "err" };
    if (hardware.conn === "open" && gpu === "ok") return { label: "Ready", tone: "ok" };
    return { label: "Degraded", tone: "warn" };
  }, [hardware.status?.gpu, hardware.conn]);

  // ---- egress sparkline ring buffer, fed at stream cadence ----
  // A new sample is pushed whenever a "stream" event updates the egress figure.
  // No random walk: the value is the real egress Mb/s (0 when absent/stopped).
  const [hist, setHist] = useState<number[]>(() => Array(HIST_LEN).fill(0));
  const egressMbps = mbps(live.stream?.egressBitsPerSec ?? null) ?? 0;
  useEffect(() => {
    setHist((h) => [...h.slice(1), egressMbps]);
  }, [live.stream, egressMbps]);

  // ---- rung enable overrides (optimistic control state) ----
  // The stream is the source of truth for a rung's `on` state, but toggling a
  // rung is a control the operator drives immediately. Until the backend echoes
  // the change back on the stream, an override wins so the toggle feels live.
  // A key clears itself once the stream reports the same value (the echo lands).
  const [rungOverride, setRungOverride] = useState<Record<string, boolean>>({});
  const streamRungs = live.rungs;
  useEffect(() => {
    setRungOverride((cur) => {
      let changed = false;
      const next: Record<string, boolean> = {};
      for (const [name, want] of Object.entries(cur)) {
        const r = streamRungs.get(name);
        // Drop the override once the stream confirms it (echo landed).
        if (r && r.on === want) {
          changed = true;
          continue;
        }
        next[name] = want;
      }
      return changed ? next : cur;
    });
  }, [streamRungs]);

  const rungs = useMemo<LiveRung[]>(() => {
    return Array.from(live.rungs.values()).map((r) =>
      r.rungName in rungOverride ? { ...r, on: rungOverride[r.rungName] } : r,
    );
  }, [live.rungs, rungOverride]);

  const onToggleRung = useCallback((rungName: string, on: boolean) => {
    setRungOverride((cur) => ({ ...cur, [rungName]: on }));
  }, []);

  // ---- rung selection drives the Rendition panel ----
  const [selRung, setSelRung] = useState<string | null>(null);
  useEffect(() => {
    if (selRung === null && rungs.length > 0) setSelRung(rungs[0].rungName);
  }, [selRung, rungs]);

  // ---- live presets (mode==="live"): the Rendition panel edits the selected ----
  const presets = useLivePresets();

  // ---- live control plane: provision a stream, arm/stop, manage destinations ----
  const control = useLiveControl();

  // Once telemetry authoritatively reports the stream on air, drop the optimistic
  // armed bridge so the control follows real state: a later disconnect returns the
  // button to "Go live" rather than leaving a stale "Stop" / "Waiting for source".
  const { armed: ctlArmed, disarm } = control;
  useEffect(() => {
    if (onAir && ctlArmed) disarm();
  }, [onAir, ctlArmed, disarm]);

  const links = useMemo(() => Array.from(live.links.values()), [live.links]);

  // Overlay live push health (from telemetry) onto the configured destinations
  // (from the control plane) by id, so a target reads its real status once the
  // stream is pushing and idles until then.
  const destHealthById = useMemo<Map<string, Tone>>(() => {
    const m = new Map<string, Tone>();
    for (const d of live.dests.values()) m.set(d.destId, d.health);
    return m;
  }, [live.dests]);

  // The primary control follows create -> go-live -> stop. "armed" (optimistic,
  // from the control hook) covers the gap between arming and the source
  // connecting; the authoritative on-air signal stays the telemetry stream.
  const stopping = control.armed || onAir;
  const canCreate = !!presets.activeId && !presets.loading;
  const ctl = !control.current
    ? {
        label: "Create stream",
        icon: "plus" as const,
        onClick: () => presets.activeId && control.create(presets.active?.name ?? "Live stream", presets.activeId),
        disabled: control.busy || !canCreate,
        title: canCreate ? "Provision a live stream and mint its key" : "Select a live preset first",
        danger: false,
      }
    : stopping
      ? {
          label: "Stop",
          icon: "square" as const,
          onClick: () => control.stop(),
          disabled: control.busy,
          title: "Stop the stream",
          danger: true,
        }
      : {
          label: "Go live",
          icon: "play" as const,
          onClick: () => control.arm(),
          disabled: control.busy,
          title: "Arm ingest for the broadcaster to connect",
          danger: false,
        };

  // The top-bar timecode reads the live session tc; an em-dash-safe clock is
  // rendered inside the monitor, but the top bar shows a compact HH:MM:SS or a
  // dash when there is no session.
  const topTc = live.stream?.tcSeconds != null ? fmtDur(live.stream.tcSeconds) : EM_DASH;

  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", background: "var(--bg-base)", color: "var(--fg1)" }}>
      <TopBar trail={[{ label: "Live transcoder" }]} mode={mode} onMode={onMode} session={session}>
        {/* Live readiness tag (from the live hardware sample + transport). It
            reads "On air" and turns red when the session is live, else it shows
            the GPU readiness like the file console. */}
        <ReadinessTag tone={readiness.tone} label={onAir ? "On air" : readiness.label} onAir={onAir} />
        <span style={{ font: "400 13px var(--font-mono)", color: "#fff", fontVariantNumeric: "tabular-nums" }}>{topTc}</span>
        <div style={{ width: 1, height: 22, background: "var(--line)" }} />
        {/* Armed but no source yet: the stream is waiting for the broadcaster to
            connect to the ingest URL. */}
        {control.armed && !onAir && (
          <span style={{ font: "var(--t-micro)", letterSpacing: ".08em", textTransform: "uppercase", color: "var(--fg3)" }}>
            Waiting for source
          </span>
        )}
        {/* A failed control call surfaces its message here rather than silently
            dropping the action (R10). */}
        {control.errorMessage && (
          <span title={control.errorMessage} style={{ font: "var(--t-micro)", color: "var(--err)", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {control.errorMessage}
          </span>
        )}
        {/* Real live control: create a stream, arm it, then stop. */}
        <button
          onClick={ctl.onClick}
          disabled={ctl.disabled}
          title={ctl.title}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            minHeight: 38,
            padding: "10px 15px",
            borderRadius: "var(--r-sm)",
            font: "var(--t-btn)",
            letterSpacing: "var(--ls-btn)",
            textTransform: "uppercase",
            color: ctl.danger ? "#fff" : ctl.disabled ? "var(--fg4)" : "var(--fg1)",
            whiteSpace: "nowrap",
            background: ctl.danger ? "var(--err)" : "transparent",
            border: ctl.danger ? "1px solid var(--err)" : "1px solid var(--line-strong)",
            cursor: ctl.disabled ? "not-allowed" : "pointer",
            opacity: ctl.disabled ? 0.6 : 1,
          }}
        >
          <Icon name={ctl.icon} size={13} fill={ctl.icon === "square" ? "currentColor" : "none"} />
          {ctl.label}
        </button>
      </TopBar>

      {/* body */}
      <div style={{ flex: 1, display: "flex", gap: 12, padding: 12, minHeight: 0 }}>
        {/* left: ingest + hardware */}
        <div style={{ width: 292, flex: "none", display: "flex", flexDirection: "column", gap: 12, minHeight: 0 }}>
          <LiveIngestPanel
            stream={live.stream}
            links={links}
            onAir={onAir}
            ingestUrl={control.ingestUrl}
            streamKey={control.streamKey}
            onSetKey={control.current ? (key) => control.setKey(control.current!.id, key) : undefined}
            busy={control.busy}
          />
          <LiveHardwarePanel hw={live.hw} />
        </div>

        {/* center: monitor + aggregate + ladder + log */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 12, flex: "none" }}>
            <LiveProgramMonitor stream={live.stream} />
            <LiveAggregatePanel
              stream={live.stream}
              hist={hist}
              onAir={onAir}
              activeStreams={live.aggregate ? live.aggregate.activeStreams : null}
            />
          </div>
          <LiveProbeGraph stream={live.stream} />
          <LiveEncoderPanel rungs={rungs} sel={selRung} onSelect={setSelRung} onToggle={onToggleRung} onAir={onAir} />
          <LivePipelineLog lines={live.logs} conn={live.conn} />
        </div>

        {/* right: rendition params (live preset) + destinations */}
        <div style={{ width: 312, flex: "none", display: "flex", flexDirection: "column", gap: 12, minHeight: 0 }}>
          <LivePresetPicker
            presets={presets.presets}
            activeId={presets.activeId}
            onChange={presets.setActiveId}
            loading={presets.loading}
            errorMessage={presets.errorMessage}
          />
          <LiveRenditionPanel preset={presets.active} onPatch={presets.patchActive} />
          <LiveDestinationsPanel
            destinations={control.destinations}
            healthById={destHealthById}
            onAdd={control.addDest}
            onRemove={control.removeDest}
            disabled={!control.current}
            busy={control.busy}
          />
        </div>
      </div>
    </div>
  );
}
